/**
 * `useClipboardHandlers` — paste, copy, drag-over, drop handlers + plain-text
 * paste intent. Extracted from PlateEditor.tsx so the main shell stays focused
 * on render orchestration rather than clipboard policy.
 *
 * Returns the four event handlers ready to attach to `<PlateContent>` /
 * `<Plate>`, plus `markNextPasteAsPlain()` — call this from the editor's
 * keydown handler when ⌘⇧V / Ctrl⇧V is detected. The paste event fires
 * after keydown, so we record intent here and consume it in `handlePaste`.
 *
 * What each handler does:
 *
 *  handlePaste:
 *    - ⌘⇧V intent: insert clipboard's plain text only, splitting on
 *      newlines into multiple paragraphs. Ignores HTML AND Slate's
 *      `application/x-slate-fragment` back-channel (which would otherwise
 *      restore every mark from the source editor). Mirrors Google Docs /
 *      Notion behavior.
 *    - Image file in clipboard: routes through `handleImageInsert` (the
 *      shared upload+insert pipeline). Toast on invalid mime / too-large.
 *    - Spreadsheet-only HTML or pure-table HTML: routes through
 *      `spreadsheetHtmlToPlateTable` for fidelity. The detection MUST run
 *      before `sanitizeOfficeHtml` strips the `<style>` blocks that Excel
 *      uses as the spreadsheet marker.
 *    - Mixed-content HTML (the common case): sanitises via `sanitizeOfficeHtml`,
 *      strips `data-slate-*` attributes (Plate's deserializer treats them
 *      as "already a slate node, skip parse rules", which silently drops
 *      every <p>/<h2> pasted from another slate-based editor and leaves
 *      only the inline leaves), then runs `deserializeHtmlElement` and
 *      inserts the resulting fragment. Plain-text fallback if dedupe fails.
 *
 *  handleDrop:
 *    - File drop of any image MIME → routes through `handleImageInsert`.
 *    - preventDefault always fires to dodge Slate's "Cannot resolve a Slate
 *      range" error on file drops.
 *
 *  handleDragOver:
 *    - Calls preventDefault when the drag payload includes Files, so the
 *      browser will dispatch the drop event to us instead of refusing it.
 *
 *  handleCopy:
 *    - Clones the current selection, runs `cleanDomForExport` (strips
 *      editor-only UI + normalises tables / images / data-slate attrs),
 *      and writes the cleaned HTML to `text/html` + plain text to
 *      `text/plain`. So pasting from this editor into Google Docs / Word
 *      / a plain-text input all produce the right shape.
 */
import { useCallback, useRef } from 'react';
import { deserializeHtmlElement } from 'platejs';
import { toast } from 'react-toastify';
import { sanitizeOfficeHtml } from '../serializers/sanitizeOfficeHtml';
import { isSpreadsheetTableHtml, isPureTableHtml, spreadsheetHtmlToPlateTable } from '../serializers/spreadsheetHtmlToPlate';
import { handleImageInsert } from '../components/ImagePlugin/handleImageInsert';
import { cleanDomForExport } from '../exporters/docxExport';

export interface UseClipboardHandlersConfig {
    editor: any;
    readOnly: boolean;
    /**
     * `uploadSource` tag passed through to `handleImageInsert` so the upload
     * pipeline knows which surface initiated the upload (one-pager, email,
     * comment, etc.) for analytics + scoping.
     */
    uploadSource: string;
}

export interface UseClipboardHandlersReturn {
    handlePaste: (e: React.ClipboardEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleCopy: (e: React.ClipboardEvent) => void;
    /**
     * Call this from the editor's keydown handler when ⌘⇧V / Ctrl⇧V is
     * detected. The paste event fires immediately after, and the next
     * `handlePaste` invocation reads this flag and switches to plain-text
     * mode. The flag is one-shot — consumed by the next paste regardless
     * of mode.
     */
    markNextPasteAsPlain: () => void;
}

export function useClipboardHandlers({ editor, readOnly, uploadSource }: UseClipboardHandlersConfig): UseClipboardHandlersReturn {
    // ⌘⇧V / Ctrl⇧V intent. Set on keydown, consumed by handlePaste, cleared
    // either way. A ref (not state) because the keydown→paste sequence is
    // synchronous and React state would update too late to be read by the
    // paste handler in the same tick.
    const plainTextPasteRef = useRef(false);

    const markNextPasteAsPlain = useCallback(() => {
        plainTextPasteRef.current = true;
    }, []);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            if (readOnly) return;

            const ed: any = editor;

            // ⌘⇧V intent: insert the plain-text payload, ignoring HTML AND
            // the `application/x-slate-fragment` back-channel (which Plate's
            // own paste handler would otherwise decode and reinstate every
            // mark). Splits on newlines so multi-line plain text becomes
            // multiple paragraphs, matching Google Docs / Notion behavior.
            if (plainTextPasteRef.current) {
                plainTextPasteRef.current = false;
                e.preventDefault();
                e.stopPropagation();
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (!text) return;
                const lines = text.split(/\r?\n/);
                lines.forEach((line, i) => {
                    if (i > 0) ed?.tf?.insertBreak?.();
                    if (line) ed?.tf?.insertText?.(line);
                });
                return;
            }

            const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
            for (const item of items) {
                if (item.kind === 'file' && item.type.startsWith('image/')) {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = item.getAsFile();
                    if (!file) return;
                    (async () => {
                        const result = await handleImageInsert(file, editor, uploadSource);
                        if (result.error === 'invalid-mime') toast.error('Invalid image file');
                        else if (result.error === 'too-large') toast.error('Image too large (max 8 MB)');
                    })();
                    return;
                }
            }

            const html = e.clipboardData?.getData('text/html') ?? '';
            if (!html) return;
            e.preventDefault();
            // Spreadsheet detection must run BEFORE sanitizeOfficeHtml strips the style blocks.
            // Only short-circuit to the table-only converter when the paste IS effectively
            // a single table — pasting a rich document (e.g. from a legacy Slate editor)
            // that mixes headings, paragraphs, and tables must fall through to the generic
            // deserializer, otherwise spreadsheetHtmlToPlateTable would collapse the entire
            // paste down to just the first table.
            if (isSpreadsheetTableHtml(html) || isPureTableHtml(html)) {
                try {
                    const tableFragment = spreadsheetHtmlToPlateTable(html);
                    if (tableFragment) {
                        ed?.tf?.insertFragment?.([tableFragment]);
                        return;
                    }
                } catch {
                    /* fall through */
                }
            }
            try {
                const cleaned = sanitizeOfficeHtml(html);
                const parsed = new DOMParser().parseFromString(cleaned, 'text/html');
                const body = parsed?.body;
                if (!body) return;
                // Strip `data-slate-*` attributes from anything pasted from
                // another slate-based editor. Plate's deserializer treats
                // `data-slate-node="element"` as "this is already a slate
                // node, skip plugin parse rules" — which causes every <p>
                // and <h2> from slate-react to be silently dropped, leaving
                // only the inline leaves. Removing the attribute lets the
                // normal validNodeName="P" / "H2" rules fire, so block
                // structure survives the paste. Plate-to-Plate paste still
                // works via the `slate-<key>` classes that Plate's renderer
                // emits separately.
                body.querySelectorAll('[data-slate-node], [data-slate-leaf], [data-slate-string], [data-slate-fragment]').forEach((el) => {
                    el.removeAttribute('data-slate-node');
                    el.removeAttribute('data-slate-leaf');
                    el.removeAttribute('data-slate-string');
                    el.removeAttribute('data-slate-fragment');
                    el.removeAttribute('data-slate-type');
                    el.removeAttribute('data-slate-inline');
                    el.removeAttribute('data-slate-void');
                    el.removeAttribute('data-slate-marks');
                });
                const fragment: any = (deserializeHtmlElement as any)(editor, body);
                if (Array.isArray(fragment) && fragment.length) {
                    ed?.tf?.insertFragment?.(fragment);
                    return;
                }
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (text) ed?.tf?.insertText?.(text);
            } catch {
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (text) ed?.tf?.insertText?.(text);
            }
        },
        // `uploadSource` belongs in deps — the original implementation in
        // PlateEditor.tsx omitted it, which would have caused stale closures
        // if the prop ever changed mid-life. Fixed during extraction.
        [editor, readOnly, uploadSource],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            if (readOnly) return;
            const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
            if (files.length === 0) return;
            // Prevent Slate's "Cannot resolve a Slate range" error on file drops.
            e.preventDefault();
            e.stopPropagation();
            const imageFiles = files.filter((f) => f.type.startsWith('image/'));
            for (const file of imageFiles) {
                (async () => {
                    const result = await handleImageInsert(file, editor, uploadSource);
                    if (result.error === 'invalid-mime') toast.error(`Invalid image file: ${file.name}`);
                    else if (result.error === 'too-large') toast.error(`Image too large (max 8 MB): ${file.name}`);
                })();
            }
        },
        // Same fix — uploadSource added to deps.
        [editor, readOnly, uploadSource],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        const types = e.dataTransfer?.types;
        if (types && Array.from(types).includes('Files')) e.preventDefault();
    }, []);

    const handleCopy = useCallback((e: React.ClipboardEvent) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        const container =
            range.commonAncestorContainer instanceof HTMLElement
                ? range.commonAncestorContainer
                : range.commonAncestorContainer.parentElement;
        if (!container?.closest?.('[data-testid="plate-editor-content"]')) return;
        e.preventDefault();
        const frag = range.cloneContents();
        const wrapper = document.createElement('div');
        wrapper.appendChild(frag);
        cleanDomForExport(wrapper);
        e.clipboardData.setData('text/html', `<html><body>${wrapper.innerHTML}</body></html>`);
        e.clipboardData.setData('text/plain', wrapper.textContent ?? '');
    }, []);

    return { handlePaste, handleDrop, handleDragOver, handleCopy, markNextPasteAsPlain };
}
